import {
  signalColors,
  signalColorsOnLight,
} from '../../../theme/foundations/tokens';

export interface WaveformBar {
  /** Bar height in px. */
  h: number;
  /** Bar color. */
  c: string;
}

const UNPLAYED = 'rgba(255,255,255,0.16)';

/**
 * Deterministic waveform generator ported from the design project's `DCLogic`
 * script. Three beating sinusoids at incommensurate frequencies give a shape
 * that reads as audio without repeating, and because it is a pure function of
 * `(index, seed)` the same project renders the same waveform on every screen —
 * that consistency is what makes the mock feel like real data.
 *
 * @param n      number of bars
 * @param seed   per-project offset; same seed => same waveform
 * @param h      max bar height in px
 * @param played playback progress 0..1. Omit for the idle (never-played) style.
 */
export function bars(
  n: number,
  seed: number,
  h: number,
  played?: number
): WaveformBar[] {
  const out: WaveformBar[] = [];
  for (let i = 0; i < n; i++) {
    const t =
      Math.sin((i + seed) * 0.41) *
      Math.sin((i + seed) * 0.13) *
      Math.cos((i + seed) * 0.77);
    const v = 0.16 + Math.abs(t) * 0.84;

    let c: string;
    if (played === undefined) {
      // Idle: accent-tinted, every 5th bar brightened to give it texture.
      c = i % 5 === 0 ? signalColors.accentHover : 'rgba(255,106,31,0.5)';
    } else {
      const on = i / n < played;
      c = on
        ? i % 4 === 0
          ? signalColors.accentBright
          : signalColors.accent
        : UNPLAYED;
    }

    out.push({ h: Math.max(2, Math.round(v * h)), c });
  }
  return out;
}

const UNPLAYED_LIGHT = 'rgba(12,14,18,0.14)';

/**
 * Same shape as `bars`, recoloured for a light surface. The dark set's
 * white-alpha unplayed bars vanish on #FFF, so the light set inverts that to a
 * black alpha and drops the accent to the darker fill tone.
 */
export function lightBars(
  n: number,
  seed: number,
  h: number,
  played?: number
): WaveformBar[] {
  return bars(n, seed, h).map((bar, i) => ({
    h: bar.h,
    c:
      played !== undefined && i / n >= played
        ? UNPLAYED_LIGHT
        : i % 5 === 0
          ? signalColorsOnLight.accentFill
          : 'rgba(240,86,11,0.42)',
  }));
}
